import React from 'react';
import {
	Container,
	SpaceBetween,
	Header,
	KeyValuePairs,
	FlashbarProps,
} from '@amzn/awsui-components-console';
import { AttributeData } from '@amzn/snow-data-service-typescript-client';

import GenericWizard from 'src/components/commonComponents/GenericWizard/GenericWizard';
import { generateReviewSectionsFromSteps } from 'src/components/commonComponents/GenericWizard/utils/wizardUtils';

import {
	ExportTaskFormData,
	getExportTaskWizardSteps,
} from './ExportTaskFormGenerationUtils';

interface ExportOperationToTaskPageFormProps {
	onCancel: () => void;
	onSubmit: (data: ExportTaskFormData) => void;
	operationId: string;
	operationName: string;
	integrationType: string;
	taskInputs: AttributeData[];
	taskOutputs: AttributeData[];
	isLoading: boolean;
	errorMessage?: string | null;
	flashBarItems?: FlashbarProps.MessageDefinition[];
}

export default function ExportOperationToTaskPageForm({
	onCancel,
	onSubmit,
	operationId,
	operationName,
	integrationType,
	taskInputs,
	taskOutputs,
	isLoading,
	errorMessage,
	flashBarItems,
}: ExportOperationToTaskPageFormProps) {
	const additionalContent = (
		<SpaceBetween size="l">
			<Container header={<Header variant="h3">Operation Details</Header>}>
				<KeyValuePairs
					columns={2}
					items={[
						{ label: 'Operation ID', value: operationId },
						{ label: 'Operation Name', value: operationName },
						{ label: 'Integration Type', value: integrationType },
					]}
				/>
			</Container>

			{taskInputs.length > 0 && (
				<Container header={<Header variant="h3">Task Input Parameters</Header>}>
					<KeyValuePairs
						columns={2}
						items={taskInputs.map((input) => ({
							label: input.attributeName || 'Unknown',
							value: `${input.attributeType || 'Unknown'} - ${
								input.description || 'No description'
							}`,
						}))}
					/>
				</Container>
			)}

			{taskOutputs.length > 0 && (
				<Container
					header={<Header variant="h3">Task Output Parameters</Header>}
				>
					<KeyValuePairs
						columns={2}
						items={taskOutputs.map((output) => ({
							label: output.attributeName || 'Unknown',
							value: `${output.attributeType || 'Unknown'} - ${
								output.description || 'No description'
							}`,
						}))}
					/>
				</Container>
			)}
		</SpaceBetween>
	);

	return (
		<GenericWizard<ExportTaskFormData>
			title="Export Operation to Task"
			description="Export an operation to create a reusable task"
			steps={getExportTaskWizardSteps()}
			onCancel={onCancel}
			onSubmit={onSubmit}
			isLoading={isLoading}
			errorMessage={errorMessage}
			flashBarItems={flashBarItems}
			additionalContent={additionalContent}
			reviewTitle="Review Export Details"
			submitButtonText="Export Operation"
			generateReviewSections={generateReviewSectionsFromSteps}
			testIdPrefix="export-operation-wizard"
		/>
	);
}